import React from 'react';
import { Form } from 'antd';
import { MinusCircleOutlined } from '@ant-design/icons';
import { FormItem } from '@xbcb/form-item-components';
import { NamePath } from '@xbcb/ui-types';
import { useTheme } from 'styled-components';
import { CharacteristicType, ProductCodeType } from '__generated__/graphql';
import ProductIdentifierFields from '../ProductIdentifierFields';
import FormListSection from '../FormListSection';
import {
  StyledSectionHeader,
  StyledFieldRow,
  StyledNamesRow,
  StyledAddonSelect,
  StyledInput,
  StyledMonthPicker,
  StyledRemoveButton,
  StyledSection,
  StyledFieldLabel,
} from './styles';

const CHARACTERISTIC_TYPE_OPTIONS = [
  { value: CharacteristicType.COLOR, label: 'Color', title: 'Product Color' },
  {
    value: CharacteristicType.DESCRIPTION,
    label: 'Description',
    title: 'Product Description',
  },
  { value: CharacteristicType.STYLE, label: 'Style', title: 'Product Style' },
];

const PRODUCT_CODE_TYPE_OPTIONS = [
  {
    value: ProductCodeType.SKU,
    label: 'SKU',
    title: 'Stock Keeping Unit',
  },
];

export interface ProductDetailsProps {
  fullNamePath?: NamePath;
  localNamePath: NamePath;
  readOnly?: boolean;
  disabled?: boolean;
}

const ProductDetails: React.FC<ProductDetailsProps> = ({
  fullNamePath,
  localNamePath,
  readOnly = false,
  disabled = false,
}) => {
  const theme = useTheme();

  return (
    <StyledSection>
      <StyledSectionHeader>Product Details</StyledSectionHeader>

      {/* Product Codes Section */}

      <StyledNamesRow>
        <FormItem
          name={[...localNamePath, 'productCodes', 0, 'code']}
          label={<StyledFieldLabel>Product Code (optional)</StyledFieldLabel>}
          $itemSize={theme.size.short_medium}
          $readOnly={readOnly}
        >
          <StyledInput
            addonBefore={
              <Form.Item
                name={[...localNamePath, 'productCodes', 0, 'type']}
                noStyle
              >
                <StyledAddonSelect
                  options={PRODUCT_CODE_TYPE_OPTIONS}
                  disabled={disabled || readOnly}
                  placeholder="Type"
                  allowClear
                />
              </Form.Item>
            }
            maxLength={19}
            disabled={disabled || readOnly}
            placeholder="Enter code"
          />
        </FormItem>
      </StyledNamesRow>

      {/* Product Identification Codes Section */}
      <StyledFieldLabel>
        Product identification code (optional)
      </StyledFieldLabel>
      <FormListSection
        name={[...localNamePath, 'itemIdentifiers']}
        addButtonLabel="Add product identification code"
        readOnly={readOnly}
        disabled={disabled}
        renderRow={(field, _index, { remove, showRemove }) => (
          <ProductIdentifierFields
            key={field.key}
            localNamePath={[field.name]}
            readOnly={readOnly}
            disabled={disabled}
            onRemove={remove}
            showRemove={showRemove}
          />
        )}
      />

      {/* Names Row: Brand Name, Product Name, Manufactured Date */}
      <StyledNamesRow>
        <FormItem
          name={[...localNamePath, 'tradeName']}
          label={<StyledFieldLabel>Brand Name (optional)</StyledFieldLabel>}
          $itemSize={theme.size.short}
          $readOnly={readOnly}
        >
          <StyledInput
            maxLength={35}
            disabled={disabled || readOnly}
            placeholder="Enter brand name"
          />
        </FormItem>
        <FormItem
          name={[...localNamePath, 'productName']}
          label={<StyledFieldLabel>Product Name</StyledFieldLabel>}
          $itemSize={theme.size.short}
          $readOnly={readOnly}
        >
          <StyledInput
            maxLength={15}
            disabled={disabled || readOnly}
            placeholder="Enter product name"
          />
        </FormItem>
        <FormItem
          name={[...localNamePath, 'manufactureDate']}
          label={
            <StyledFieldLabel>Manufactured date (MM/YYYY)</StyledFieldLabel>
          }
          $itemSize={theme.size.short}
          $readOnly={readOnly}
        >
          <StyledMonthPicker
            picker="month"
            format="MM/YYYY"
            disabled={disabled || readOnly}
            placeholder="Select date"
          />
        </FormItem>
      </StyledNamesRow>

      {/* Product Characteristics Section */}
      <StyledFieldLabel>Product Characteristics (optional)</StyledFieldLabel>
      <FormListSection
        name={[...localNamePath, 'characteristics']}
        addButtonLabel="Add product characteristics"
        readOnly={readOnly}
        disabled={disabled}
        renderRow={(field, _index, { remove, showRemove }) => (
          <StyledFieldRow key={field.key}>
            <FormItem
              name={[field.name, 'description']}
              $itemSize={theme.size.short_medium}
              $readOnly={readOnly}
            >
              <StyledInput
                addonBefore={
                  <Form.Item name={[field.name, 'type']} noStyle>
                    <StyledAddonSelect
                      options={CHARACTERISTIC_TYPE_OPTIONS}
                      disabled={disabled || readOnly}
                      placeholder="Type"
                      allowClear
                    />
                  </Form.Item>
                }
                maxLength={57}
                disabled={disabled || readOnly}
                placeholder="Enter description"
              />
            </FormItem>
            {showRemove && (
              <StyledRemoveButton
                type="text"
                danger
                icon={<MinusCircleOutlined />}
                onClick={remove}
                disabled={disabled}
              >
                Remove
              </StyledRemoveButton>
            )}
          </StyledFieldRow>
        )}
      />
    </StyledSection>
  );
};

export default ProductDetails;
